import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Draggable from 'react-draggable'
import { useTranslation } from 'react-i18next'

import Text from 'particles/Text'
import styled from 'particles/themes/styled'
import IconButton from 'particles/Button/IconButton'
import { modalZIndex } from 'particles/Modal'
import ReadOnlyStepsEditor from './ReadOnlyStepsEditor'
import {
    GLOBAL_MODAL_TYPE,
    closeModal,
    openModalWithProps,
    selectBriefModalProps,
    selectIsModalOpen,
} from 'state/constants'
import { selectSelectedCampaignUuid } from 'state/review'
import { TaskState } from 'state/tasks'
import { useLocation } from 'react-router'

const MODAL_WIDTH = 444
const MODAL_HEIGHT = 640

export const DraggableBriefModal = () => {
    const isModalOpen = useSelector(selectIsModalOpen(GLOBAL_MODAL_TYPE.BRIEF_REQUIREMENT))
    const campaignUuid = useSelector(selectSelectedCampaignUuid)
    const briefModalProps = useSelector(selectBriefModalProps)
    const dispatch = useDispatch()
    const [t] = useTranslation()
    const location = useLocation()

    const defaultPosition = {
        x: window.innerWidth / 2 - MODAL_WIDTH / 2,
        y: window.innerHeight / 2 - MODAL_HEIGHT / 2,
    }

    const close = () => {
        dispatch(closeModal())
    }

    // to close modal if user change route location
    useEffect(() => {
        close()
    }, [location])

    if (!isModalOpen || !campaignUuid || !briefModalProps?.stepId || !briefModalProps.task) return null

    const { task, stepId } = briefModalProps
    const taskWithActiveStepOnly: TaskState = {
        ...task,
        steps: task.steps.filter(value => value.uuid === stepId),
    }

    return (
        <Draggable
            key="briefModalContainer"
            handle="#briefModalContainer"
            defaultPosition={defaultPosition}
            bounds="parent"
        >
            <ModalContainer>
                <div className="brief-modal">
                    <IconButton
                        icon="x"
                        iconForm="regular"
                        form="icon"
                        color="neutral"
                        onClick={close}
                        className="close-btn"
                        size="l"
                    />

                    <div className="brief-modal-title" id="briefModalContainer">
                        <Text scale="H3">{t('modalWindow.submissionsRequirementsTitle')}</Text>
                    </div>
                    <div className="brief-modal-content">
                        <ReadOnlyStepsEditor task={taskWithActiveStepOnly} />
                    </div>
                </div>
            </ModalContainer>
        </Draggable>
    )
}

interface BriefRequirementsProps {
    task: TaskState
    stepId: string
}

const BriefRequirements = ({ task, stepId }: BriefRequirementsProps) => {
    const isModalOpen = useSelector(selectIsModalOpen(GLOBAL_MODAL_TYPE.BRIEF_REQUIREMENT))
    const dispatch = useDispatch()
    const [t] = useTranslation()

    const openBriefModal = () => {
        if (isModalOpen) {
            dispatch(closeModal())
        }

        // using set timeout, so react doesn't batch dispatch
        // need to trigger re render for default modal position, in case the user resize window & the modal is outside the screen
        setTimeout(() => {
            dispatch(
                openModalWithProps({
                    type: GLOBAL_MODAL_TYPE.BRIEF_REQUIREMENT,
                    task,
                    stepId,
                }),
            )
        }, 0)
    }

    return (
        <Text scale="bodyS700" color="action" onClick={openBriefModal} style={{ cursor: 'pointer' }}>
            {t('button.viewSubmissionRequirements')}
        </Text>
    )
}

export default BriefRequirements

const ModalContainer = styled.div`
    position: fixed;
    z-index: ${modalZIndex - 1};
    background: ${({ theme }) => theme.colors.surface.primary};
    border-radius: 4px;
    box-sizing: border-box;
    top: 0px;
    left: 0px;
    min-width: 343px;
    min-height: 360px;
    max-width: 62.5vw;
    max-height: 94vh;
    box-shadow: 0px 0px 12px 0px #0000001f;

    .brief-modal {
        display: flex;
        flex-direction: column;
        position: relative;
        height: 100%;
        padding-top: 12px;

        .close-btn {
            position: absolute;
            top: 10px;
            right: 10px;
            z-index: 10;
        }

        .brief-modal-title {
            cursor: grab;
            box-sizing: border-box;
            // change cursor on text component
            div {
                cursor: grab;
            }
            height: 54px;
            padding: 24px;
            padding-bottom: 0px;
        }

        .brief-modal-content {
            height: 100%;
            z-index: 9;
            padding-bottom: 24px;
            overflow: auto;
            resize: both;
            overflow: auto;

            width: ${MODAL_WIDTH}px;
            height: ${MODAL_HEIGHT - 78}px;
            max-width: 100%;
            min-width: 100%;
            max-height: calc(90vh - 78px);
            min-height: calc(360px - 78px);

            &::-webkit-scrollbar {
                width: 12px;
            }
            &::-webkit-scrollbar-thumb {
                background: rgba(0, 0, 0, 0.6);
                border-radius: 8px;
                border: 4px solid rgba(0, 0, 0, 0);
                background-clip: padding-box;
            }
            &::-webkit-scrollbar-track {
                background: ${({ theme }) => theme.colors.surface.primary};
                box-shadow: 1px 0px 0px 0px #dedede inset;
                padding: 4px;
                width: 12px;
            }
        }
    }
`
